import { getFontCss, styleConstants } from '@paywithextend/components'
import React, { useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import { ExpenseCategoriesContext } from 'src/components/ExpenseCategoriesInputBox/ExpenseCategoriesInputBox'
import ExpenseCategoriesInputs from 'src/components/ExpenseCategoriesInputs/ExpenseCategoriesInputs'
import { EXPENSE_CATEGORY_EMPTY_OPTION } from 'src/components/ExpenseCategoryDropdown/ExpenseCategoryDropdown'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import { useFormFields, useScrollToScrollGroupRefs } from 'src/hooks'
import Trns from 'src/localization/Trns'
import { ExpenseDetailsModel } from 'src/models/transaction.models'
import {
  ExpenseCategoryType,
  useQueryExpenseCategories,
} from 'src/queries/organizations/expenseCategories.queries'
import { selectIsSelectAllTransactionsFromState } from 'src/redux/selectors/data/transactions.selectors'
import { css } from 'styled-components'

interface TransactionExpenseDetailsFormProps {
  className?: string
  expenseDetails?: Array<Array<ExpenseDetailsModel | undefined>>
  patchingTransaction: boolean
  onSuccessfulSubmit: (expenseDetails: Record<string, string>) => void
  onCancel: () => void
  hideWasOnUpdate?: boolean
  explicitSelectNone?: boolean
}

const TransactionExpenseDetailsForm: React.FC<
  TransactionExpenseDetailsFormProps
> = (props): React.ReactElement => {
  const { data: expCatSchema, isPending } = useQueryExpenseCategories({
    filters: { active: true },
  })
  const allSelected = useSelector(selectIsSelectAllTransactionsFromState)

  const initialExpCatFields = useMemo((): Record<
    string,
    Array<ExpenseDetailsModel | undefined>
  > => {
    return props.expenseDetails !== undefined
      ? props.expenseDetails.reduce((acc, curr) => {
          const expCat =
            curr.length > 0
              ? curr.filter((cat): cat is ExpenseDetailsModel => !!cat)[0]
              : undefined
          const isCustomerOrVendor =
            expCat?.categoryType === ExpenseCategoryType.Customer ||
            expCat?.categoryType === ExpenseCategoryType.Vendor
          const schemaEC = isCustomerOrVendor
            ? expCatSchema?.find((ec) => ec.type === expCat?.categoryType)
            : undefined
          const schemaId = isCustomerOrVendor && schemaEC ? schemaEC.id : ''
          if (expCat) {
            return {
              ...acc,
              // use schemaId for vendor/customer (found via type) since those dont have categoryIds
              [expCat.categoryId || schemaId]: curr,
            }
          } else {
            return acc
          }
        }, {})
      : {}
  }, [props.expenseDetails, expCatSchema])

  const expCatFieldValidation = (): Record<
    string,
    Array<(val?: string) => React.ReactElement | undefined>
  > => {
    return {}
  }

  const formatBaseValuesToSelected = (
    vals: Record<string, Array<ExpenseDetailsModel | undefined>>,
  ): Record<string, string | undefined> => {
    return (
      expCatSchema?.reduce((acc, curr) => {
        // map multiple values to undefined so that only 1 option appears selected in the dropdown
        const baseVal =
          vals?.[curr.id] !== undefined && vals?.[curr.id].length === 1
            ? Array.from(vals?.[curr.id])[0]
            : undefined
        return {
          ...acc,
          // use labelName if no id, for free text values
          [curr.id]: baseVal?.labelId || baseVal?.labelName,
        }
      }, {}) || {}
    )
  }

  const {
    fields: expCatFields,
    setFields: setExpCatFields,
    fieldsErrors: expCatFieldsErrors,
    onFieldChange: onExpCatFieldChange,
    setErrorsAndReturnDidPassValidation: expCatFieldsSetErrors,
    topmostScrollGroup: expCatFieldsTopmostScrollGroup,
  } = useFormFields<Record<string, string | undefined>>(
    {},
    expCatFieldValidation,
  )

  useEffect((): void => {
    if (expCatSchema !== undefined && !allSelected) {
      setExpCatFields(formatBaseValuesToSelected(initialExpCatFields))
    }
  }, [initialExpCatFields])

  const scrollRef0 = useRef<HTMLFormElement>(null)
  useScrollToScrollGroupRefs([scrollRef0], [expCatFieldsTopmostScrollGroup])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    if (expCatFieldsSetErrors()) {
      props.onSuccessfulSubmit(
        Object.fromEntries(
          Object.entries(expCatFields)
            .map(([catId, labelId]) => {
              const ec = expCatSchema?.find((ec) => ec.id === catId)

              if (
                props.explicitSelectNone &&
                ec?.freeTextAllowed &&
                !ec?.hasLabels &&
                !labelId
              ) {
                // TODO: is free text empty unchanged or set to nothing?
                if (initialExpCatFields[catId]?.length > 1) {
                  // if we would show "Multiple Values", treat empty as unchanged
                  return [catId, undefined]
                } else {
                  // if they are all the same or there are none to begin with, treat empty as empty
                  return [catId, EXPENSE_CATEGORY_EMPTY_OPTION]
                }
              }
              return [catId, labelId]
            })
            .filter(
              (categoryLabelMap): categoryLabelMap is [string, string] =>
                !!categoryLabelMap[1],
            ),
        ),
      )
    }
  }

  return (
    <form
      ref={scrollRef0}
      onSubmit={onSubmit}
      css={css`
        & > :nth-child(n + 2) {
          margin-top: 40px;
        }
      `}
    >
      {isPending ? (
        <LoadingBackgroundPage />
      ) : expCatSchema?.length === 0 ? (
        <p
          css={css`
            ${getFontCss(
              16,
              styleConstants.colors.red,
              styleConstants.fontFamilies.regular,
            )}
          `}
        >
          <Trns
            source={
              'There are no longer any supported Expense Categories for this transaction. Pressing "Update and Remove Expense Categories" will remove all Expense Categories tied to this transaction. ${ifYouWant}'
            }
            expressions={{
              ifYouWant: (
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      styleConstants.colors.red,
                      styleConstants.fontFamilies.medium,
                    )}
                  `}
                >
                  <Trns
                    source={
                      'If you want to keep these Expense Categories for your records, press "Cancel" instead.'
                    }
                  />
                </span>
              ),
            }}
          />
        </p>
      ) : (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            & > :nth-child(n + 2) {
              margin-top: 20px;
            }
          `}
        >
          <ExpenseCategoriesInputs
            context={ExpenseCategoriesContext.TRANSACTION}
            showUpdates
            hideWasOnUpdate={props.hideWasOnUpdate}
            initialExpDetailsMultiple={initialExpCatFields}
            expenseDetailsErrors={expCatFieldsErrors}
            expenseDetails={expCatFields}
            onExpenseCategoryChange={(catId: string, labelId: string): void => {
              onExpCatFieldChange(catId, labelId)
            }}
            deselectExpenseCategory={
              props.explicitSelectNone
                ? undefined
                : (catId: string): void => {
                    const newExpenseCategories = { ...expCatFields }
                    delete newExpenseCategories[catId]
                    setExpCatFields(newExpenseCategories)
                  }
            }
            explicitSelectNone={props.explicitSelectNone}
            enforceRequired={false}
          />
        </div>
      )}
      <div
        css={css`
          display: flex;
          align-items: center;
        `}
      >
        <Button
          testId={'editTxnSave'}
          design={ButtonDesigns.designCta}
          type={ButtonTypes.submit}
          disabled={isPending}
          text={
            expCatSchema !== undefined && expCatSchema.length === 0 ? (
              <Trns source={'Update and Remove Expense Categories'} />
            ) : (
              <Trns source={'Save Updates'} />
            )
          }
          loading={props.patchingTransaction}
        />
        <HoverableLink
          testId={'editTxnCancel'}
          onClick={props.onCancel}
          ariaLabelSource={'Cancel'}
          css={css`
            margin-left: 20px;
          `}
        >
          <Trns source={'Cancel'} />
        </HoverableLink>
      </div>
    </form>
  )
}

export default TransactionExpenseDetailsForm
